import { useNavigate } from 'react-router-dom';
import { useOnboardingContext } from '@/contexts/useOnboardingContext';
import { ENGLISH_COMFORT_OPTIONS, CONCERN_OPTIONS } from '@/types/profile';
import { track } from '@/lib/analytics';
import Button from '@/components/ui/Button';
import OnboardingShell from '@/components/layout/OnboardingShell';

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Not scheduled';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getEnglishLabel(value: string | undefined): string {
  if (!value) return 'Not set';
  const option = ENGLISH_COMFORT_OPTIONS.find((o) => o.value === value);
  return option ? `${option.icon} ${option.label}` : value;
}

function getConcernLabels(values: string[] | undefined | null): string {
  if (!values || values.length === 0) return 'None selected';
  return values
    .map((v) => {
      const option = CONCERN_OPTIONS.find((o) => o.value === v);
      return option ? `${option.icon} ${option.label}` : v;
    })
    .join(', ');
}

export default function ValuePreviewPage() {
  const navigate = useNavigate();
  const { state, setStep } = useOnboardingContext();

  const handleContinue = () => {
    setStep(6);
    track('onboarding_step_completed', { step: 'value_preview' });
    navigate('/onboarding/quick-win');
  };

  return (
    <OnboardingShell
      step={5}
      title={<>Here&apos;s your personalized plan</>}
      subtitle={<>We&apos;ll tailor your experience based on what you shared.</>}
    >
      <div className="rounded-2xl border-2 border-border bg-card p-6 md:p-8 grid sm:grid-cols-2 gap-x-8 gap-y-5">
        <div>
          <p className="font-sans text-sm text-text-secondary mb-1">Name</p>
          <p className="font-sans font-medium text-text-primary">{state.full_name || 'Not provided'}</p>
        </div>

        <div>
          <p className="font-sans text-sm text-text-secondary mb-1">Interview date</p>
          <p className="font-sans font-medium text-text-primary">{formatDate(state.interview_date)}</p>
        </div>

        <div>
          <p className="font-sans text-sm text-text-secondary mb-1">English level</p>
          <p className="font-sans font-medium text-text-primary">{getEnglishLabel(state.english_comfort_level)}</p>
        </div>

        <div>
          <p className="font-sans text-sm text-text-secondary mb-1">Concerns</p>
          <p className="font-sans font-medium text-text-primary">{getConcernLabels(state.nervous_about)}</p>
        </div>
      </div>

      <div className="mt-8">
        <Button fullWidth onClick={handleContinue}>
          Continue
        </Button>
      </div>
    </OnboardingShell>
  );
}
